import { BaseDialogComponent } from './../../../../../components/kit/base-dialog/base-dialog.component';
import {
    PersonAuthorizationEnum,
    personAuthorizationEnumOptions,
} from './../../../../../../../../types/src/services/enum/person-authorization.enum';
import { Component, inject, OnInit, output, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { isInvalid } from '../../../../shared/helpers/validate';
import { fb } from '../../../../shared/shared.global-service';
import { dateify } from '../../../../shared/helpers/date.helper';
import { PersonAuthorizationsDialogStore } from './person-authorizations-dialog.store';
import { contentGlobalSuccess } from '../../../content.error';
import {
    AddressDetailObject,
    CreatePersonContactInput,
    CreateUserInput,
    PersonContactGridItemObject,
    UpdatePersonContactInput,
} from '@it2go/types';
import { removeTypename } from '../../../../shared/utils/object.utils';
import { PersonContactTypeEnum } from '@libs/types/src/services/enum/person-contact-type.enum';
import { AddressInputComponent } from '../../../../../components/kit/input/address-input/address-input.component';
import { CheckInputComponent } from '../../../../../components/kit/input/check-input/check-input.component';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import { EmailInputComponent } from '../../../../../components/kit/input/text-input/email-input.component';
import { PhoneInputComponent } from '../../../../../components/kit/input/phone-input/phone-input.component';
import { SelectInputComponent } from '../../../../../components/kit/input/select-input/select-input.component';
import { TextInputComponent } from '../../../../../components/kit/input/text-input/text-input.component';
import { TranslatePipe } from '@ngx-translate/core';

export interface PersonAuthorizationDialogData {
    authorization?: any | null;
    personId?: number | null;
}

@Component({
    selector: 'app-person-authorizations-dialog',
    templateUrl: './person-authorizations-dialog.component.html',
    styleUrls: ['./person-authorizations-dialog.component.scss'],
    providers: [PersonAuthorizationsDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        AddressInputComponent,
        CheckInputComponent,
        DateInputComponent,
        EmailInputComponent,
        PhoneInputComponent,
        ReactiveFormsModule,
        SelectInputComponent,
        TextInputComponent,
        TranslatePipe,
    ],
})
export class PersonAuthorizationsDialogComponent extends BaseDialogComponent implements OnInit {

    public readonly store = inject(PersonAuthorizationsDialogStore);

    public authorization: PersonContactGridItemObject | null = null;
    public personId: number | null = null;

    public created = output<any>();

    protected form = fb.group({
        validFrom: fb.control<Date | null>(null, [Validators.required]),
        validTo: fb.control<Date | null>(null, []),
        authorizationType: fb.control<string | null>(null, [Validators.required]),

        titlesBefore: fb.control<string | null>(null),
        name: fb.control<string | null>(null, [Validators.required]),
        surname: fb.control<string | null>(null, [Validators.required]),
        titlesAfter: fb.control<string | null>(null),
        mobile: fb.control<string | null>(null),
        phone: fb.control<string | null>(null),
        email: fb.control<string | null>(null, Validators.email),

        address: fb.control<AddressDetailObject | null>(null),

        basis: fb.control<string | null>(null),
        referenceNumber: fb.control<string | null>(null),
        isRelative: fb.control<boolean>(false),

        municipality: fb.control<number | null>(null),

        note: fb.control<string | null>(null),
    });

    public ngOnInit(): void {
        this.authorization = this.dialogConfig.data?.authorization;
        this.personId = this.dialogConfig.data?.personId;

        if (this.authorization) {
            this.form.patchValue({
                validFrom: dateify(this.authorization.validFrom),
                validTo: dateify(this.authorization.validTo),
                authorizationType: this.authorization.subType,

                titlesBefore: this.authorization.user.titleBeforeName,
                name: this.authorization.user.name,
                surname: this.authorization.user.surname,
                titlesAfter: this.authorization.user.titleAfterName,
                mobile: this.authorization.user.mobile,
                phone: this.authorization.user.phone,
                email: this.authorization.user.email,
                address: this.authorization.user.address,

                basis: this.authorization.data?.basis || null,
                referenceNumber: this.authorization.data?.referenceNumber || null,
                isRelative: this.authorization.data?.isRelative || false,

                municipality: this.authorization.data?.municipality || null,

                note: this.authorization.note,
            });
            this.form.get('titlesBefore')?.disable();
            this.form.get('name')?.disable();
            this.form.get('surname')?.disable();
            this.form.get('titlesAfter')?.disable();
            this.form.get('mobile')?.disable();
            this.form.get('phone')?.disable();
            this.form.get('email')?.disable();
            this.form.get('address')?.disable();
        }
    }

    public override async save(): Promise<void> {
        if (this.authorization) {
            await this.updatePersonAuthorization();
        } else {
            await this.savePersonAuthorization();
        }
    }

    public async updatePersonAuthorization(): Promise<void> {
        if (isInvalid(this.form)) return;

        const authorization: UpdatePersonContactInput = {
            id: this.authorization!.id,
            subType: this.form.value.authorizationType!,
            validFrom: this.form.value.validFrom!.toISOString(),
            validTo: this.form.value.validTo?.toISOString() || null,
            note: this.form.value.note || null,
            data: {
                basis: this.form.value.basis,
                referenceNumber: this.form.value.referenceNumber,
                isRelative: this.form.value.isRelative,
                municipality: this.form.value.municipality,
            },
        };

        this.personId && await this.store.api.updatePersonContact(authorization);
        contentGlobalSuccess.next('common.updated');
        this.dialogRef.close(authorization);
    }

    public async savePersonAuthorization(): Promise<void> {
        if (isInvalid(this.form)) return;

        const userInput: CreateUserInput = {
            email: this.form.value.email || null,
            name: this.form.value.name || '',
            surname: this.form.value.surname || '',
            titleBeforeName: this.form.value.titlesBefore || null,
            titleAfterName: this.form.value.titlesAfter || null,
            mobile: this.form.value.mobile || null,
            phone: this.form.value.phone || null,
            address: removeTypename(this.form.value.address),
            password: null,
            doctor: false,
            icpOfDoctor: null,
            organizationId: this.store.globalGlobalStore.organizationStore.selectedId(),
        };
        const user = await this.store.api.createUser(userInput);

        // TODO: check if user already exists as it is in create person contact

        if (user && this.personId) {
            const authorization: CreatePersonContactInput = {
                userId: user.id,
                folderId: null,
                personId: this.personId,
                type: PersonContactTypeEnum.AUTHORIZATION,
                subType: this.form.value.authorizationType!,
                validFrom: this.form.value.validFrom!.toISOString(),
                validTo: this.form.value.validTo?.toISOString() || null,
                note: this.form.value.note || null,
                relation: '',
                data: {
                    basis: this.form.value.basis || null,
                    referenceNumber: this.form.value.referenceNumber || null,
                    isRelative: this.form.value.isRelative || false,
                    municipality: this.form.value.municipality || null,
                },
            };

            this.personId && await this.store.api.createPersonContact(authorization);
            contentGlobalSuccess.next('common.created');
            this.dialogRef.close(authorization);
        }
    }

    protected readonly personAuthorizationEnumOptions = personAuthorizationEnumOptions;
    protected readonly AuthorizationEnum = PersonAuthorizationEnum;
}
